import {
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { INITIAL_PERIOD, MAP_LEGENDS } from "constants/GeneralConstants";
import { useObjectState } from "hooks";
import { useRecoilValue } from "recoil";
import { currentTransactionTrackerRegion } from "data/Atoms";

const HeatMapContext = createContext();

export const HeatMapContextProvider = ({ children }) => {
  const Region = useRecoilValue(currentTransactionTrackerRegion);
  const [map, setMap] = useState();
  const [showNotesToggle, setShowNotes] = useState(false);
  const [mapMode, setMapMode] = useState("transactions_count");
  const [selectedLegend, setSelectedLegend] = useState("");
  const [heatmapModals, setHeatmapModals] = useState();
  const [searchKeys, setSearchKeys] = useObjectState({
    period: String(INITIAL_PERIOD),
    fromDate: null,
    toDate: null,
  });

  const getSelectedLegends = useCallback(
    (modeType) => {
      const selectedItem = MAP_LEGENDS.find((item) => item.mode === modeType);
      setSelectedLegend(selectedItem);
    },
    [mapMode]
  );

  const toggleShowNotes = useCallback(() => {
    setShowNotes((prev) => !prev);
  }, [showNotesToggle]);

  useEffect(() => {
    if (!heatmapModals) {
      const url = import.meta.env.VITE_APP_MAP_STYLE_MODE;
      fetch(url)
        .then((d) => d.json())
        .then((d) => setHeatmapModals(d));
    }
  }, [heatmapModals]);

  useEffect(() => {
    const currentRegionLayer = Region?.mapKey?.toLowerCase();
    if (mapMode) {
      const m = map?.current?.getMap();
      if (mapMode === "transactions_count") {
        m?.setPaintProperty(`parcels@${currentRegionLayer}`, "fill-opacity", 0);
        m?.setPaintProperty("parcels@malham", "fill-opacity", 0);
      } else {
        m?.setPaintProperty(
          `parcels@${currentRegionLayer}`,
          "fill-color",
          heatmapModals?.[mapMode]?.["fill-color"]
        );
        m?.setPaintProperty(
          "parcels@malham",
          "fill-color",
          heatmapModals?.[mapMode]?.["fill-color"]
        );
        m?.setPaintProperty(
          `parcels@${currentRegionLayer}`,
          "fill-opacity",
          0.6
        );
        m?.setPaintProperty("parcels@malham", "fill-opacity", 0.6);
      }
      getSelectedLegends(mapMode);
    }
  }, [mapMode, map, heatmapModals, Region]);

  const value = useMemo(
    () => ({
      map,
      setMap,
      showNotesToggle,
      toggleShowNotes,
      mapMode,
      setMapMode,
      heatmapModals,
      selectedLegend,
      setSelectedLegend,
      getSelectedLegends,
      searchKeys,
      setSearchKeys,
    }),
    [
      map,
      setMap,
      showNotesToggle,
      toggleShowNotes,
      mapMode,
      setMapMode,
      heatmapModals,
      selectedLegend,
      setSelectedLegend,
      getSelectedLegends,
      searchKeys,
      setSearchKeys,
    ]
  );

  return (
    <HeatMapContext.Provider value={value}>{children}</HeatMapContext.Provider>
  );
};

export const useHeatMapContext = () => {
  return useContext(HeatMapContext);
};
